import React from 'react';
import { ArrowRight } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { useQuoteModal } from '../../contexts/QuoteModalContext';

export function PageShell({ children, testid }) {
  return (
    <div className="bg-black min-h-screen">
      <Header />
      <main className="pt-28" data-testid={testid}>{children}</main>
      <Footer />
    </div>
  );
}

export function PageHero({ eyebrow, title, accent, intro, img, children }) {
  return (
    <section className="relative px-6 pt-16 pb-16 md:pt-24 md:pb-24">
      <div className={`max-w-[1400px] mx-auto grid gap-12 items-center ${img ? 'lg:grid-cols-12' : ''}`}>
        <div className={img ? 'lg:col-span-7' : 'max-w-4xl'}>
          {eyebrow && <p className="text-xs uppercase tracking-[0.25em] text-[#00b8ff] mb-6" data-testid="page-eyebrow">{eyebrow}</p>}
          <h1 className="font-display text-[#f4efe6] uppercase text-[52px] sm:text-[80px] md:text-[110px] leading-[0.9] tracking-tight" data-testid="page-title">
            {title}{accent && <> <span className="font-script italic text-[#00b8ff] normal-case">{accent}</span></>}
          </h1>
          {intro && <p className="text-[#cfc8bb] text-base md:text-lg max-w-2xl mt-8 leading-relaxed">{intro}</p>}
          {children}
        </div>
        {img && (
          <div className="lg:col-span-5 aspect-[4/5] rounded-md overflow-hidden shadow-2xl">
            <img src={img} alt={`${title}${accent ? ` ${accent}` : ''}`} className="w-full h-full object-cover" />
          </div>
        )}
      </div>
    </section>
  );
}

// Visible marker for copy the owner still needs to supply.
export function Placeholder({ children }) {
  return (
    <span className="inline-block rounded border border-dashed border-[#00b8ff]/50 bg-[#00b8ff]/5 px-2 py-0.5 text-[#7fd8ff] text-[0.95em]" data-testid="content-placeholder">
      [{children}]
    </span>
  );
}

export function QuoteButton({ label = 'Get a Production Quote', testid = 'page-quote-btn' }) {
  const { openModal } = useQuoteModal();
  return (
    <button
      type="button"
      onClick={openModal}
      className="group inline-flex items-center justify-center gap-2 bg-[#00b8ff] text-black rounded-full px-8 py-4 font-semibold text-[15px] hover:bg-[#f4efe6] transition-colors duration-200"
      data-testid={testid}
    >
      {label}
      <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
    </button>
  );
}

export function QuoteBand({ title = 'Ready to make it?', label }) {
  return (
    <section className="px-6 py-24">
      <div className="max-w-[1400px] mx-auto rounded-2xl border border-white/10 bg-gradient-to-br from-[#00b8ff]/15 to-transparent p-10 md:p-16 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
        <h2 className="font-display text-[#f4efe6] uppercase text-4xl md:text-6xl tracking-tight leading-[0.95]">{title}</h2>
        <QuoteButton label={label} testid="quote-band-btn" />
      </div>
    </section>
  );
}

export function PlaceholderList({ items }) {
  return (
    <section className="px-6 pb-8">
      <div className="max-w-[1400px] mx-auto grid gap-6 md:grid-cols-2">
        {items.map((it) => (
          <div key={it.title} className="rounded-xl border border-white/10 p-8 bg-white/[0.02]" data-testid={`info-card-${it.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
            <h2 className="font-display text-[#f4efe6] uppercase text-2xl md:text-3xl tracking-tight mb-4">{it.title}</h2>
            <p className="text-[#cfc8bb] leading-relaxed">{it.text || <Placeholder>{it.placeholder}</Placeholder>}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
